const lifecycleSteps = [
  { number: "01", title: "Chairside case creation", detail: "A clinician creates a fictional restoration case in simulated desktop software and records the tooth, restoration type, material, and clinical intent." },
  { number: "02", title: "Scan capture", detail: "Demonstration geometry represents an intraoral scan and prepared anatomy. No real patient information is used." },
  { number: "03", title: "Secure cloud upload", detail: "The desktop application packages case metadata and geometry, then submits them through an authenticated API." },
  { number: "04", title: "Automated processing", detail: "Cloud services validate the package, store geometry, generate previews, and start an event-driven workflow." },
  { number: "05", title: "Interactive 3D design", detail: "The DCube workspace opens the case for inspection, restoration planning, design adjustments, and guided learning." },
  { number: "06", title: "Review and collaboration", detail: "A clinician, technician, instructor, or learner can review the design, comment, request revisions, and approve the result." },
  { number: "07", title: "Manufacturing preparation", detail: "An approved design moves to a simulated milling or 3D-printing workflow with traceable status updates." },
  { number: "08", title: "Delivery and completion", detail: "The chairside system receives the final status, records delivery, and closes the demonstration case lifecycle." },
];

const cloudServices = [
  "Identity and role-based access",
  "Case and workflow services",
  "Encrypted geometry storage",
  "Event-driven processing",
  "Review, comments, and approvals",
  "Notifications and audit history",
  "Manufacturing integration",
  "Learning analytics and LMS integration",
];

const roadmap = [
  { phase: "Available now", status: "Prototype", title: "Interactive 3D restoration workspace", text: "A working browser-based environment using sample cases, mock data, and demonstration dental geometry." },
  { phase: "Next phase", status: "Planned", title: "Guided end-to-end case simulation", text: "A clickable fictional case that moves from chairside creation through cloud processing, design, review, manufacturing, and completion." },
  { phase: "Future phase", status: "Concept", title: "Connected desktop and cloud platform", text: "A secure chairside desktop connector, production APIs, automated workflows, collaboration, auditability, and manufacturing integrations." },
];

export function ArchitecturePage() {
  return (
    <div className="page architecture-page">
      <section className="architecture-hero">
        <div>
          <div className="architecture-kicker">PLATFORM VISION</div>
          <h1>From Dental Chair to Finished Restoration</h1>
          <p>
            A proposed end-to-end architecture connecting chairside desktop software,
            secure cloud processing, interactive 3D design, professional review,
            manufacturing, and delivery.
          </p>
        </div>
        <div className="architecture-hero__status">
          <span>Current state</span>
          <strong>Interactive prototype</strong>
          <small>Future capabilities are clearly marked as planned or conceptual.</small>
        </div>
      </section>

      <section className="architecture-disclaimer" role="note">
        <strong>Demonstration environment</strong>
        <span>
          All patient names, clinical details, scans, and restoration geometry shown in
          this prototype are fictional and are used only to demonstrate the proposed
          workflow. This is not a clinical system or diagnostic tool.
        </span>
      </section>

      <section className="architecture-section">
        <div className="architecture-section__heading">
          <div>
            <div className="architecture-kicker">COMPLETE LIFECYCLE</div>
            <h2>A connected restoration journey</h2>
          </div>
          <p>
            The planned experience follows one fictional case from the dental chair to
            completion, making both the technology and the learning workflow visible.
          </p>
        </div>

        <div className="lifecycle-grid">
          {lifecycleSteps.map((step) => (
            <article className="lifecycle-step" key={step.number}>
              <div className="lifecycle-step__number">{step.number}</div>
              <div>
                <h3>{step.title}</h3>
                <p>{step.detail}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="architecture-section">
        <div className="architecture-section__heading">
          <div>
            <div className="architecture-kicker">SYSTEM ARCHITECTURE</div>
            <h2>Desktop-to-cloud platform</h2>
          </div>
          <p>
            The design separates chairside capture, cloud services, the web-based design
            workspace, and downstream manufacturing so each area can evolve independently.
          </p>
        </div>

        <div className="architecture-flow" aria-label="Proposed desktop-to-cloud architecture">
          <div className="architecture-node architecture-node--primary">
            <span>CHAIRSIDE</span>
            <strong>Desktop Application</strong>
            <small>Case creation · scan capture · secure submission</small>
          </div>
          <div className="architecture-arrow"><span>HTTPS / REST API</span>→</div>
          <div className="architecture-node architecture-node--cloud">
            <span>CLOUD PLATFORM</span>
            <strong>Workflow and Data Services</strong>
            <small>Identity · storage · automation · audit trail</small>
          </div>
          <div className="architecture-arrow"><span>Case + geometry</span>→</div>
          <div className="architecture-node">
            <span>WEB EXPERIENCE</span>
            <strong>DCube Design Workspace</strong>
            <small>3D design · learning · review · collaboration</small>
          </div>
          <div className="architecture-arrow"><span>Approved output</span>→</div>
          <div className="architecture-node">
            <span>FULFILLMENT</span>
            <strong>Manufacturing Integration</strong>
            <small>Milling · printing · delivery status</small>
          </div>
        </div>

        <div className="service-grid">
          {cloudServices.map((service) => (
            <div className="service-item" key={service}>
              <span aria-hidden="true">✓</span>
              <strong>{service}</strong>
            </div>
          ))}
        </div>
      </section>

      <section className="architecture-section">
        <div className="architecture-section__heading">
          <div>
            <div className="architecture-kicker">DELIVERY ROADMAP</div>
            <h2>Prototype today, connected platform over time</h2>
          </div>
          <p>
            This roadmap distinguishes working functionality from the proposed next steps
            so visitors can understand both the current achievement and the broader vision.
          </p>
        </div>

        <div className="roadmap-grid">
          {roadmap.map((item) => (
            <article className="roadmap-card" key={item.phase}>
              <div className="roadmap-card__topline">
                <span>{item.phase}</span>
                <em className={`roadmap-status roadmap-status--${item.status.toLowerCase()}`}>
                  {item.status}
                </em>
              </div>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="architecture-section architecture-learning">
        <div>
          <div className="architecture-kicker">LEARNING APPLICATIONS</div>
          <h2>The same lifecycle can become a guided teaching experience</h2>
          <p>
            Using fictional cases makes it possible to teach digital dentistry without
            exposing patient data. Each stage can include explanations, decisions,
            instructor feedback, assessments, and repeatable simulations.
          </p>
        </div>
        <ul>
          <li>Guided clinical case walkthroughs</li>
          <li>Interactive 3D anatomy and restoration concepts</li>
          <li>Role-based dentist, technician, and learner experiences</li>
          <li>Instructor review and collaborative feedback</li>
          <li>Progress tracking and future LMS integration</li>
        </ul>
      </section>
    </div>
  );
}
